import {
  Disposable,
  DisposableContainer
} from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { EntityWithEntityName } from '../EntityWithEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
import { EntityFilter } from './EntityFilter';
import { EventDispatcher } from 'record-it-utils/events/EventDispatcher/EventDispatcher';

export class EntityFilterContainer<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> {
  private readonly createEntityFilter: CreateEntityFilter<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;

  private readonly dispatcher = new EventDispatcher<
    EventDispatcherConfig<TSynchronizationEnvironmentTypes>
  >();

  private readonly disposableContainer = new DisposableContainer();

  private filter: EntityFilter<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  > | null = null;

  constructor({
    createEntityFilter
  }: {
    createEntityFilter: CreateEntityFilter<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >;
  }) {
    this.createEntityFilter = createEntityFilter;
  }

  public init(): void {
    this.filter = this.createEntityFilter();
    this.disposableContainer.add(
      this.filter.subscribe({
        updateAllEntities: () => {
          this.handleUpdateAllEntities();
        },
        updateEntity: (entityName) => {
          this.handleUpdateEntity(entityName);
        }
      })
    );

    this.handleUpdateAllEntities();
  }

  public destroy(): void {
    this.filter = null;
    this.disposableContainer.disposeAll();
  }

  public isEntityVisible<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >({
    entity,
    entityName
  }: EntityWithEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >): boolean {
    const filterFunction =
      this.filter?.entityNameToIsVisibleFunction[entityName];

    if (!filterFunction) {
      return true;
    }

    return filterFunction({ entity });
  }

  public registerUpdateListener({
    entityName,
    callback
  }: {
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
    callback: () => void;
  }): Disposable {
    return this.dispatcher.addEventListener(entityName, callback);
  }

  private handleUpdateAllEntities(): void {
    for (const entityName of Object.keys(
      this.getRequiredFilter().entityNameToIsVisibleFunction
    )) {
      this.dispatcher.dispatchEvent(entityName, undefined);
    }
  }

  private handleUpdateEntity(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  ): void {
    if (!this.getRequiredFilter().entityNameToIsVisibleFunction[entityName]) {
      throw new EntityFilterTryingToUpdateEntityWithoutIsVisibleFunctionError(
        entityName
      );
    }

    this.dispatcher.dispatchEvent(entityName, undefined);
  }

  private getRequiredFilter(): EntityFilter<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  > {
    if (!this.filter) {
      throw new Error(
        'no filter available, is the EntityFilterContainer initialized?'
      );
    }

    return this.filter;
  }
}

export class EntityFilterTryingToUpdateEntityWithoutIsVisibleFunctionError extends Error {
  constructor(entityName: string) {
    super(
      `The EntityFilter is trying to update ${entityName} but it has no is visible function for this entity.`
    );
  }
}

export type CreateEntityFilter<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = () => EntityFilter<
  TSynchronizationEnvironmentTypes,
  TEntityTypesByEntityName
>;

type EventDispatcherConfig<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = {
  [key in TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]: undefined;
};
